import React, { useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Upload, ScanBarcode, Sparkles, Camera, Search, Keyboard, History, Trash2, ChevronRight } from "lucide-react";
import DNALoader from "../components/DNALoader";
import BarcodeScanner from "../components/BarcodeScanner";
import { api, slugify } from "../lib/api";
import { decodeImageFile, normalizeProductCode } from "../lib/barcode";
import { readHistory, addHistory, clearHistory } from "../lib/history";

const ratingColor = (r) => (r === "red" ? "#FF3B3B" : r === "yellow" ? "#FFB800" : "#00C853");

const timeAgo = (iso) => {
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  return `${Math.round(h / 24)} d ago`;
};

export default function Scan() {
  const [code, setCode] = useState("");
  const [status, setStatus] = useState(null); // { level, text }
  const [loading, setLoading] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [notFound, setNotFound] = useState(null);
  const [history, setHistory] = useState(readHistory);
  const fileRef = useRef(null);
  const manualRef = useRef(null);
  const navigate = useNavigate();

  const resolve = async (barcode) => {
    const clean = normalizeProductCode(barcode);
    if (!clean) {
      setStatus({ level: "err", text: "That doesn't look like a valid barcode. Enter 8, 12 or 13 digits (EAN/UPC)." });
      return;
    }
    setNotFound(null);
    setLoading(true);
    setStatus({ level: "info", text: `Looking up barcode ${clean}...` });
    try {
      const { data } = await api.get(`/barcode/${encodeURIComponent(clean)}`);
      const d = data.data || {};
      const productSlug = d.slug || slugify(d.productName);
      setHistory(addHistory({ barcode: clean, slug: productSlug, productName: d.productName, overallScore: d.overallScore, overallRating: d.overallRating }));
      setStatus({ level: "ok", text: `Analysis complete for ${d.productName}` });
      navigate(`/product/${productSlug}`);
    } catch (e) {
      const httpStatus = e?.response?.status;
      const msg = e?.response?.data?.detail || e.message || "Lookup failed";
      if (httpStatus === 404) {
        setStatus(null);
        setNotFound({ code: clean, text: msg });
      } else {
        setStatus({ level: "err", text: msg });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setNotFound(null);
    setStatus({ level: "info", text: "Decoding barcode from photo..." });
    try {
      const found = await decodeImageFile(f);
      if (!found) {
        setStatus({ level: "err", text: "No barcode found in this photo, try a sharper, closer photo." });
        return;
      }
      setCode(found);
      await resolve(found);
    } catch (err) {
      console.error("[scanner] photo decode error:", err);
      setStatus({ level: "err", text: "No barcode found in this photo, try a sharper, closer photo." });
    }
  };

  const openScanner = () => {
    setNotFound(null);
    setStatus(null);
    setScannerOpen(true);
  };

  const onDetected = (detected) => {
    setScannerOpen(false);
    setCode(detected);
    resolve(detected);
  };

  const focusManual = () => {
    setTimeout(() => manualRef.current?.focus(), 50);
  };

  const submitManual = (e) => {
    e.preventDefault();
    resolve(code);
  };

  return (
    <div data-testid="scan-page" className="max-w-3xl mx-auto px-6 md:px-10 py-12 md:py-16">
      <BarcodeScanner
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onDetected={onDetected}
        onUpload={() => fileRef.current?.click()}
        onManual={focusManual}
      />

      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-[color:var(--text-3)] mb-3">
          <ScanBarcode className="h-3.5 w-3.5" /> Barcode lookup
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tighter">
          Scan the <span className="text-[color:var(--lime)]">barcode</span>
        </h1>
        <p className="mt-3 text-[color:var(--text-2)] max-w-xl mx-auto">
          Use your camera, upload a photo, or type the number. We'll look it up on OpenFoodFacts, then let Claude pull apart every declared ingredient.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <button
          onClick={openScanner}
          className="surface rounded-xl p-6 text-left border-[color:var(--lime)]/40 hover:border-[color:var(--lime)] transition-colors"
          data-testid="scan-camera-btn"
        >
          <Camera className="h-6 w-6 text-[color:var(--lime)] mb-3" strokeWidth={1.5} />
          <div className="font-display font-semibold mb-1">Use camera</div>
          <div className="text-xs text-[color:var(--text-2)]">Live scan — point at the barcode (browser permission required)</div>
        </button>
        <button
          onClick={() => fileRef.current?.click()}
          className="surface rounded-xl p-6 text-left hover:border-[color:var(--lime)]/40 transition-colors"
          data-testid="scan-upload-btn"
        >
          <Upload className="h-6 w-6 text-[color:var(--lime)] mb-3" strokeWidth={1.5} />
          <div className="font-display font-semibold mb-1">Upload a photo</div>
          <div className="text-xs text-[color:var(--text-2)]">Any image of the packaging barcode</div>
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFile}
          data-testid="scan-file-input"
        />
      </div>

      <form onSubmit={submitManual} className="surface rounded-xl p-5 mb-6">
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[color:var(--text-3)] mb-2">
          Or enter barcode manually
        </div>
        <div className="flex gap-2">
          <input
            ref={manualRef}
            data-testid="scan-manual-input"
            className="flex-1 min-w-0 surface rounded-lg px-3 py-2 outline-none focus:border-[color:var(--lime)] font-mono text-sm"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 14))}
            placeholder="e.g. 3017620422003"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="off"
          />
          <button
            type="submit"
            className="lime-btn rounded-lg px-4 py-2 text-sm inline-flex items-center gap-2"
            data-testid="scan-manual-submit"
          >
            <Sparkles className="h-3.5 w-3.5" /> Analyze
          </button>
        </div>
      </form>

      {loading && <DNALoader label="Cross-referencing OpenFoodFacts..." />}

      {notFound && (
        <div data-testid="scan-not-found" className="surface rounded-xl p-5 mb-6">
          <div className="font-display font-semibold mb-1" style={{ color: "#FFB020" }}>
            We couldn't find this product or its ingredients
          </div>
          <p className="text-sm text-[color:var(--text-2)] mb-4">
            Barcode <span className="font-mono">{notFound.code}</span> — {notFound.text}
          </p>
          <div className="flex flex-wrap gap-2">
            <button data-testid="not-found-scan-again" onClick={openScanner} className="lime-btn rounded-lg px-4 py-2 text-sm inline-flex items-center gap-2">
              <Camera className="h-4 w-4" /> Scan again
            </button>
            <button data-testid="not-found-manual" onClick={focusManual} className="ghost-btn rounded-lg px-4 py-2 text-sm inline-flex items-center gap-2">
              <Keyboard className="h-4 w-4" /> Enter barcode manually
            </button>
            <button data-testid="not-found-search" onClick={() => navigate("/")} className="ghost-btn rounded-lg px-4 py-2 text-sm inline-flex items-center gap-2">
              <Search className="h-4 w-4" /> Search by name
            </button>
          </div>
        </div>
      )}

      {history.length > 0 && (
        <section data-testid="scan-history" className="surface rounded-xl p-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-[color:var(--text-3)]">
              <History className="h-3.5 w-3.5" /> Recent scans
            </div>
            <button
              data-testid="scan-history-clear"
              onClick={() => setHistory(clearHistory())}
              className="text-xs text-[color:var(--text-3)] hover:text-[#FF3B3B] inline-flex items-center gap-1 transition-colors"
            >
              <Trash2 className="h-3.5 w-3.5" /> Clear
            </button>
          </div>
          <ul className="divide-y divide-white/5">
            {history.map((h) => (
              <li key={h.barcode}>
                <Link
                  to={`/product/${h.slug}`}
                  data-testid={`scan-history-item-${h.barcode}`}
                  className="flex items-center gap-3 py-2.5 group"
                >
                  <span
                    className="font-mono text-xs font-semibold w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                    style={{ color: ratingColor(h.overallRating), background: `${ratingColor(h.overallRating)}1a` }}
                  >
                    {h.overallScore ?? "–"}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-medium truncate group-hover:text-[color:var(--lime)] transition-colors">{h.productName}</span>
                    <span className="block font-mono text-[11px] text-[color:var(--text-3)]">{h.barcode} · {timeAgo(h.scannedAt)}</span>
                  </span>
                  <ChevronRight className="h-4 w-4 text-[color:var(--text-3)] group-hover:text-[color:var(--lime)] transition-colors" />
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {status && (
        <div
          data-testid="scan-status"
          className="surface rounded-lg p-3 text-sm"
          style={{
            color:
              status.level === "err"
                ? "#FF3B3B"
                : status.level === "ok"
                ? "#00C853"
                : "var(--text-2)",
          }}
        >
          {status.text}
        </div>
      )}
    </div>
  );
}
